// src/app/core/auth/session-evaluation.ts
//
// Núcleo de decisión del control de sesión — funciones puras, sin Angular,
// sin RxJS, sin DOM. Toda la fontanería (SessionService) es una capa
// delgada alrededor de estas dos funciones. Esto es lo que hace que el
// comportamiento sea verificable con aritmética, no cronometrando.

export type SessionState = 'active' | 'warning' | 'expired';

/**
 * Decide el estado de la sesión a partir de una única muestra de tiempo
 * transcurrido. No conoce relojes de pared ni monótonos — solo resta.
 *
 * Un delta negativo (nowMs < lastActivityMs) nunca se interpreta como
 * "acaba de haber actividad": un control de seguridad falla cerrando, no
 * abriendo. Quien llama a esta función (SessionService, vía
 * resolveElapsedMs) es responsable de que nowMs/lastActivityMs ya reflejen
 * la regla del doble reloj — esta función solo aplica la defensa final.
 */
export function evaluateSession(
  nowMs: number,
  lastActivityMs: number,
  timeoutMs: number,
  warningMs: number,
): SessionState {
  const elapsedMs = nowMs - lastActivityMs;

  if (elapsedMs < 0) {
    return 'expired';
  }
  if (elapsedMs >= timeoutMs) {
    return 'expired';
  }
  if (elapsedMs >= warningMs) {
    return 'warning';
  }
  return 'active';
}

/** Una muestra conjunta de reloj de pared y reloj monótono, tomada en el mismo instante. */
export interface ClockSample {
  readonly wallMs: number;
  readonly monoMs: number;
}

/**
 * Resuelve el tiempo transcurrido real entre dos muestras de reloj (defecto
 * D6, regla del doble reloj — §1.2.1 del prompt). Ninguno de los dos
 * relojes sirve solo:
 *
 * - Date.now() (wallMs) sobrevive a la suspensión del equipo, pero no es
 *   inmune a un ajuste manual de hora ni a la sincronización NTP.
 * - performance.now() (monoMs) es monótono (inmune a saltos de hora), pero
 *   su comportamiento durante la suspensión depende del navegador/SO.
 *
 * Regla única: ante cualquier discrepancia, se toma el delta MAYOR de los
 * dos. Una anomalía de reloj puede acortar una sesión, nunca prolongarla.
 */
export function resolveElapsedMs(now: ClockSample, lastActivity: ClockSample): number {
  const wallDeltaMs = now.wallMs - lastActivity.wallMs;
  const monoDeltaMs = now.monoMs - lastActivity.monoMs;
  return Math.max(wallDeltaMs, monoDeltaMs);
}
